import PageHeader from "@/components/PageHeader";
import { Calendar, Clock, MapPin, Sparkles, Users, Music, MessageCircle, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import ShareButton from "@/components/ShareButton";

const expect = [
  { icon: Sparkles, label: "Games" },
  { icon: Users, label: "Community" },
  { icon: Music, label: "Worship" },
  { icon: MessageCircle, label: "A Short Message" },
];

const GatheringsPage = () => {
  return (
    <>
      <PageHeader
        overline="Mark Your Calendar"
        title="Next"
        accent="Gathering."
        subtitle="One night. Every high schooler welcome. Come as you are and bring a friend."
        testId="gatherings-header"
        bgImage="https://images.unsplash.com/photo-1663886667374-1cbcaf1e4756?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0"
      />

      <section className="relative pb-16" data-testid="gathering-details-section">
        <div className="glow-blue top-0 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div
            className="card-glass p-8 md:p-14 relative overflow-hidden"
            data-testid="gathering-details-card"
            style={{
              background:
                "linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(59,130,246,0.08) 100%), rgba(31, 28, 26, 0.85)",
            }}
          >
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-forest-glow/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-deepblue-glow/10 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-forest-glow/30 bg-forest-glow/5 mb-6" data-testid="gathering-theme-badge">
                  <span className="w-2 h-2 rounded-full bg-forest-glow animate-pulse" />
                  <p className="overline" style={{ letterSpacing: "0.2em" }}>This Month · Prayer + Worship Night</p>
                </div>
                <p className="overline mb-4">You're Invited</p>
                <h2 className="font-heading font-black text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                  Saturday,<br />
                  <span className="text-forest-glow">November 7th</span>
                </h2>
                <p className="font-heading font-bold text-2xl md:text-3xl uppercase mt-6 tracking-tight">
                  6:30 <span className="text-deepblue-glow">PM</span>
                </p>
                <div className="hr-line my-8" />
                <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-xl">
                  This month we're setting it all aside — no games, no distractions. Just a night
                  of <span className="text-forest-glow font-semibold">prayer</span> and{" "}
                  <span className="text-deepblue-glow font-semibold">worship</span>, together.
                  Come hungry for what God wants to do.
                </p>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.04]" data-testid="gathering-date-block">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar size={18} className="text-forest-glow" />
                    <p className="overline">Date</p>
                  </div>
                  <p className="font-heading font-bold text-xl">Saturday · November 7th</p>
                </div>

                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.04]" data-testid="gathering-time-block">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock size={18} className="text-deepblue-glow" />
                    <p className="overline">Time</p>
                  </div>
                  <p className="font-heading font-bold text-xl">6:30 PM</p>
                </div>

                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.04]" data-testid="gathering-location-block">
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin size={18} className="text-forest-glow" />
                    <p className="overline">Location</p>
                  </div>
                  <a
                    href="https://maps.google.com/?q=3324+West+Cary+Street+Suite+C+Richmond+VA"
                    target="_blank"
                    rel="noreferrer"
                    className="font-heading font-bold text-xl hover:text-forest-glow transition-colors block"
                    data-testid="gathering-location-address"
                  >
                    3324 West Cary Street,<br />Suite C
                  </a>
                  <p className="text-sm text-white/50 mt-2">
                    Questions? DM <span className="text-forest-glow">@rvastudents</span> or email{" "}
                    <a
                      href="mailto:studentsofrva@gmail.com"
                      className="text-deepblue-glow hover:underline break-all"
                      data-testid="gathering-location-email"
                    >
                      studentsofrva@gmail.com
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP FORM */}
      <section id="rsvp" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden scroll-mt-24" data-testid="gathering-rsvp-section">
        <div className="glow-green top-20 -left-40 opacity-40" />
        <div className="glow-blue bottom-20 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:pt-4">
            <p className="overline mb-4">Let Us Know</p>
            <h2 className="font-heading font-black text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
              Are You<br />
              <span className="text-forest-glow">Coming</span>?
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
              Take 30 seconds to fill this out so we know to expect you (and how much pizza to
              buy). No pressure — just helps us plan.
            </p>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSc-srHijw6zBb9wgadprogFUlKilgYHYTyKlgwKqwUWB-nCfg/viewform?usp=publish-editor"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost mt-8"
              data-testid="gathering-rsvp-open-link"
            >
              Open Form In New Tab <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="lg:col-span-7" data-testid="gathering-rsvp-wrapper">
            <div className="card-glass p-2 md:p-3 relative overflow-hidden">
              <div className="rounded-2xl overflow-hidden bg-white/[0.04]">
                <iframe
                  src="https://docs.google.com/forms/d/e/1FAIpQLSc-srHijw6zBb9wgadprogFUlKilgYHYTyKlgwKqwUWB-nCfg/viewform?embedded=true"
                  width="100%"
                  height="900"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="RSVP for RVA Students Gathering"
                  data-testid="gathering-rsvp-iframe"
                  className="w-full block"
                >
                  Loading…
                </iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32" data-testid="gathering-expect-section">
        <div className="glow-green top-20 -left-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="overline mb-4">What To Expect</p>
              <h2 className="font-heading font-black text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
                Loud. Real.<br /><span className="text-deepblue-glow">Worth Showing Up For.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6">
              <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
                At a gathering, you can expect games, community, worship, a short message,
                and time to connect with other students.{" "}
                <span className="text-forest-glow font-semibold">Come as you are and bring a friend.</span>
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-4">
                {expect.map((e, i) => {
                  const Icon = e.icon;
                  return (
                    <div
                      key={e.label}
                      className="p-5 rounded-2xl border border-white/10 bg-white/[0.04] flex items-center gap-4 hover:border-forest-glow/40 transition-colors"
                      data-testid={`expect-item-${i}`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                        <Icon size={18} className={i % 2 === 0 ? "text-forest-glow" : "text-deepblue-glow"} />
                      </div>
                      <p className="font-heading font-semibold text-sm uppercase tracking-widest">
                        {e.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 border-t border-white/10 text-center" data-testid="gathering-cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <p className="overline mb-6">Bring The Squad</p>
          <h3 className="font-heading font-black text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight mb-8">
            Follow along for<br />
            <span className="text-forest-glow">real-time updates</span>.
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://instagram.com/rvastudents"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="gathering-instagram-cta"
            >
              @rvastudents on Instagram <ArrowUpRight size={16} />
            </a>
            <ShareButton label="Text A Friend" testId="gathering-share-button" />
          </div>
          <div className="mt-4">
            <Link to="/contact" className="text-sm text-white/60 hover:text-white transition-colors font-heading uppercase tracking-widest" data-testid="gathering-contact-link">
              Or reach out directly →
            </Link>
          </div>
        </div>
      </section>

      {/* PAST NIGHTS PREVIEW → Recap page */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="gathering-recap-preview">
        <div className="glow-green top-1/2 -left-40 -translate-y-1/2 opacity-40" />
        <div className="glow-blue top-0 -right-40 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="overline mb-4">The Story So Far</p>
              <h3 className="font-heading font-black text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
                <span className="text-forest-glow">Two nights</span>.<br />
                One city.<br />
                One <span className="text-deepblue-glow">Jesus</span>.
              </h3>
              <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
                From the first backyard gathering to now — take a look at what God has been
                doing through RVA Students.
              </p>
              <Link to="/recap" className="btn-primary mt-8" data-testid="gathering-view-recap-button">
                See The Recaps <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
              <Link
                to="/recap"
                className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-white/10 group col-span-1 row-span-2"
                data-testid="recap-preview-photo-1"
              >
                <img
                  src="/images/sep26/pool-prayer.webp"
                  alt="Students gathered around the pool in prayer"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </Link>
              <Link
                to="/recap"
                className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10 group"
                data-testid="recap-preview-photo-2"
              >
                <img
                  src="/images/july31/guys-group.jpeg"
                  alt="Students at Night One"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </Link>
              <Link
                to="/recap"
                className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10 group"
                data-testid="recap-preview-photo-3"
              >
                <img
                  src="/images/sep26/driveway.webp"
                  alt="Students at Night Two"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GatheringsPage;
